import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  setCustomerDefaultPersistence,
  deleteCustomerDefaultPersistence,
  deletePersistence,
} from '@api/persistence';
import { useAtom } from 'jotai';
import { persistedOverviewAtom } from '../atoms';
import { message } from 'antd';
import { OVERVIEW_PERSISTENCE_KEY } from '../constants';

/**
 * Hook for admin operations on customer default metrics
 */
export const useOverviewDefaultConfig = () => {
  const queryClient = useQueryClient();
  const [persistedOverview] = useAtom(persistedOverviewAtom);

  const setCustomerDefaultMutation = useMutation(
    (config: any) => setCustomerDefaultPersistence(config),
    {
      onSuccess: () => {
        message.success('Overview default configuration saved successfully');
        // Invalidate persistence query to refresh data
        queryClient.invalidateQueries(['persistence', OVERVIEW_PERSISTENCE_KEY]);
      },
      onError: (error: any) => {
        message.error('Failed to save overview default configuration');
        // eslint-disable-next-line no-console
        console.error('Error setting overview default:', error);
      },
    },
  );

  const deleteCustomerDefaultMutation = useMutation(
    () => deleteCustomerDefaultPersistence({ key: OVERVIEW_PERSISTENCE_KEY }),
    {
      onSuccess: () => {
        message.success('Overview default configuration deleted successfully');
        // Invalidate persistence query to refresh data
        queryClient.invalidateQueries(['persistence', OVERVIEW_PERSISTENCE_KEY]);
      },
      onError: (error: any) => {
        message.error('Failed to delete overview default configuration');
        // eslint-disable-next-line no-console
        console.error('Error deleting overview default:', error);
      },
    },
  );

  const applyToAll = () => {
    if (!persistedOverview) {
      message.error('No configuration to apply');
      return;
    }

    setCustomerDefaultMutation.mutate({
      configuration: persistedOverview,
      key: OVERVIEW_PERSISTENCE_KEY,
    });
  };

  const removeCustomerDefault = () => {
    deleteCustomerDefaultMutation.mutate();
  };

  return {
    applyToAll,
    removeCustomerDefault,
    isSettingDefault: setCustomerDefaultMutation.isLoading,
    isDeletingDefault: deleteCustomerDefaultMutation.isLoading,
  };
};

/**
 * Hook for resetting user configuration to default
 * Deletes the user's personal persistence, falling back to customer default or system default
 */
export const useResetToDefault = () => {
  const queryClient = useQueryClient();

  const resetMutation = useMutation(
    async () => {
      // Delete user's personal persistence for overview dashboard
      
      return deletePersistence(OVERVIEW_PERSISTENCE_KEY);
    },
    {
      onSuccess: () => {
        message.success('Configuration reset to default');
        // Invalidate and refetch persistence data
        queryClient.invalidateQueries(['persistence', OVERVIEW_PERSISTENCE_KEY]);
        // Reload the page to ensure clean state
        window.location.reload();
      },
      onError: (error: any) => {
        message.error('Failed to reset configuration');
        // eslint-disable-next-line no-console
        console.error('Error resetting configuration:', error);
      },
    },
  );

  const resetToDefault = () => {
    resetMutation.mutate();
  };

  return {
    resetToDefault,
    isResetting: resetMutation.isLoading,
  };
};
